import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { getAllEmployees, getEmployeeDetails } from '../../services/api';

const normalizeId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id);
};

const hasValue = (id) => normalizeId(id) !== '';

const buildListParams = (apiFilters) => {
  if (!apiFilters) return {};
  const { department, designation, status } = apiFilters;
  return {
    department: department || undefined,
    designation: designation || undefined,
    status: status || undefined,
  };
};

const normalizeEmployee = (emp) => {
  if (!emp || emp.id === undefined || emp.id === null) return null;
  const fullName = emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.username || '';
  return {
    id: emp.id,
    full_name: fullName,
    employee_id: emp.employee_id || '',
  };
};

export default function FormEmployeeSearchField({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  required,
  fullWidth,
  size,
  error,
  helperText,
  apiFilters,
  initialEmployee,
  debounceMs,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState(() => normalizeEmployee(initialEmployee));
  const [open, setOpen] = useState(false);

  const debounceRef = useRef(null);
  const employeeCacheRef = useRef({});
  const hasLoadedInitialListRef = useRef(false);

  const listParams = useMemo(() => buildListParams(apiFilters), [apiFilters]);
  const listParamsKey = JSON.stringify(listParams);

  const mergedOptions = useMemo(() => {
    if (!selected) return options;
    const exists = options.some((opt) => normalizeId(opt.id) === normalizeId(selected.id));
    return exists ? options : [selected, ...options];
  }, [options, selected]);

  const getEmployeeLabel = useCallback((option) => {
    if (!option) return '';
    if (option.full_name && option.employee_id) {
      return `${option.full_name}  |  ${option.employee_id}`;
    }
    return option.full_name || `${option.id ?? ''}`;
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  useEffect(() => {
    const normalized = normalizeEmployee(initialEmployee);
    if (normalized) {
      employeeCacheRef.current[normalizeId(normalized.id)] = normalized;
    }
  }, [initialEmployee]);

  useEffect(() => {
    if (!hasValue(value)) {
      if (selected !== null) setSelected(null);
      return undefined;
    }

    if (selected && normalizeId(selected.id) === normalizeId(value)) return undefined;

    const cached = employeeCacheRef.current[normalizeId(value)];
    if (cached) {
      setSelected(cached);
      return undefined;
    }

    const initialNormalized = normalizeEmployee(initialEmployee);
    if (initialNormalized && normalizeId(initialNormalized.id) === normalizeId(value)) {
      setSelected(initialNormalized);
      return undefined;
    }

    const fromOptions = options.find((opt) => normalizeId(opt.id) === normalizeId(value));
    if (fromOptions) {
      setSelected(fromOptions);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await getEmployeeDetails(value);
        const raw = res?.data?.employee || res?.data;
        const normalized = normalizeEmployee(raw);
        if (!normalized || cancelled) return;
        employeeCacheRef.current[normalizeId(normalized.id)] = normalized;
        setSelected(normalized);
      } catch (err) {
        console.error('Error loading employee details:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, initialEmployee, options, selected]);

  useEffect(() => {
    if (!open) {
      setInputValue(selected ? getEmployeeLabel(selected) : '');
    }
  }, [selected, getEmployeeLabel, open]);

  const loadAllEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllEmployees(listParams);
      const results = (res?.data?.results || []).map(normalizeEmployee).filter(Boolean);
      results.forEach((emp) => {
        employeeCacheRef.current[normalizeId(emp.id)] = emp;
      });
      setOptions(results);
      hasLoadedInitialListRef.current = true;
    } catch (err) {
      console.error('Error loading employees:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParamsKey]);

  useEffect(() => {
    hasLoadedInitialListRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParamsKey]);

  const runSearch = useCallback(
    (query) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      const trimmed = (query || '').trim();
      if (trimmed.length < 1) {
        loadAllEmployees();
        return;
      }

      setLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await getAllEmployees({ search: trimmed, ...listParams });
          const results = (res?.data?.results || []).map(normalizeEmployee).filter(Boolean);
          results.forEach((emp) => {
            employeeCacheRef.current[normalizeId(emp.id)] = emp;
          });
          setOptions(results);
        } catch (err) {
          console.error('Error searching employees:', err);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceMs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listParamsKey, debounceMs, loadAllEmployees]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    setInputValue('');
    if (!hasLoadedInitialListRef.current && options.length === 0) {
      loadAllEmployees();
    } else if (options.length > 0) {
      loadAllEmployees();
    }
  }, [options.length, loadAllEmployees]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setInputValue(selected ? getEmployeeLabel(selected) : '');
  }, [getEmployeeLabel, selected]);

  const handleInputChange = useCallback(
    (event, nextValue, reason) => {
      setInputValue(nextValue);
      if (reason === 'input') {
        if (selected && hasValue(value)) {
          setSelected(null);
          onChange('');
        }
        runSearch(nextValue);
      }
    },
    [runSearch, selected, value, onChange]
  );

  const handleChange = useCallback(
    (event, nextValue) => {
      if (!nextValue) {
        setSelected(null);
        onChange('');
        return;
      }
      const normalized = normalizeEmployee(nextValue);
      if (!normalized) return;
      employeeCacheRef.current[normalizeId(normalized.id)] = normalized;
      setSelected(normalized);
      onChange(normalized.id);
      setInputValue('');
      setOpen(false);
    },
    [onChange]
  );

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled}
      value={selected}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={mergedOptions}
      loading={loading}
      filterOptions={(x) => x}
      clearOnBlur={false}
      blurOnSelect
      isOptionEqualToValue={(option, val) => normalizeId(option?.id) === normalizeId(val?.id)}
      getOptionLabel={getEmployeeLabel}
      noOptionsText={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : inputValue.trim().length > 0 ? (
          'No employees match your search'
        ) : (
          'No employees'
        )
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="body2" fontWeight={500}>
              {option.full_name}
            </Typography>
            {option.employee_id && (
              <Typography variant="caption" color="text.secondary">
                {option.employee_id}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

FormEmployeeSearchField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  error: PropTypes.bool,
  helperText: PropTypes.node,
  apiFilters: PropTypes.shape({
    department: PropTypes.string,
    designation: PropTypes.string,
    status: PropTypes.string,
  }),
  initialEmployee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    employee_id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
  debounceMs: PropTypes.number,
};

FormEmployeeSearchField.defaultProps = {
  value: '',
  label: 'Employee',
  placeholder: 'Search or select employee…',
  disabled: false,
  required: false,
  fullWidth: true,
  size: 'medium',
  error: false,
  helperText: undefined,
  apiFilters: undefined,
  initialEmployee: undefined,
  debounceMs: 400,
};
